import { exhaustiveFail } from '@cbridge-utils/error';
import {
  IApiLpConnectionBase, IApiLpConnectionState, ApiLpConnectionStatus, IApiLpAdapterSetting, IApiLpConnection,
} from '@cbridge-packages/api';
import {
  IAdminApiLp, IAdminApiLpCreate, IApiGatewayLp, ApiGatewayLpStatus, IAdminApiLpDetails, ApiLpErrorResolutionPolicy,
} from '@cbridge-packages/api-admin';
import { DEMO_CBRIDGE_ADAPTER } from '@cbridge-packages/lp-adapters';
import {
  ILp, ILpForm, IGatewayLp, LpGatewayStatus, ILpConnectionState, LpConnectionStatus, ILpConnectionForm, ILpAdapterSetting,
} from './lp.interface';

export namespace LpTranslator {
  export function fromApi(lp: IAdminApiLp): ILp {
    return {
      lpId: lp.id,
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval,
      errorResolutionPolicy: lp.errorResolution?.policy ?? ApiLpErrorResolutionPolicy.Manual,
      resetSeqNumFlagSupported: lp.errorResolution?.resetSeqNumFlagSupported ?? false,
      pricing: LpTranslator.fromApiLpConnectionState(lp.pricing),
      trading: LpTranslator.fromApiLpConnectionState(lp.trading),
      gateways: lp.gateways?.map(LpTranslator.fromApiGatewayLp),
    };
  }

  export function fromApiLpConnectionState(state: IApiLpConnectionState): ILpConnectionState {
    switch (state.status) {
      case ApiLpConnectionStatus.Error:
        return { ...state, status: LpConnectionStatus.Error };
      case ApiLpConnectionStatus.Offline:
        return { ...state, status: LpConnectionStatus.Offline };
      case ApiLpConnectionStatus.Online:
        return { ...state, status: LpConnectionStatus.Online };
      case ApiLpConnectionStatus.Backup:
        return { ...state, status: LpConnectionStatus.Backup };
      default:
        exhaustiveFail(state.status);
    }
  }

  export function fromApiGatewayLp(gwLp: IApiGatewayLp): IGatewayLp {
    return {
      id: gwLp.id,
      name: gwLp.name,
      available: gwLp.available,
      status: gwLp.available
        ? LpTranslator.fromApiGatewayStatus(gwLp.status)
        : LpGatewayStatus.Error,
    };
  }

  export function fromApiGatewayStatus(status: ApiGatewayLpStatus): LpGatewayStatus {
    if (status === ApiGatewayLpStatus.Active) {
      return LpGatewayStatus.Active;
    } else {
      return LpGatewayStatus.Backup;
    }
  }

  // CP omits the connections (or their fields) for the DEMO_CBRIDGE adapter
  export function fromApiConnection(lpConnection?: IApiLpConnection): ILpConnectionForm {
    const adapterSettings: ILpAdapterSetting[] = (lpConnection?.adapter?.settings ?? [])
      .map((item, index) => ({ ...item, id: index }));

    return {
      host: lpConnection?.host,
      port: lpConnection?.port,
      login: lpConnection?.login,
      password: lpConnection?.password,
      adapterSettings,
    };
  }

  export function fromApiDetails(lp: IAdminApiLpDetails): ILpForm {
    return {
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval ?? null,
      errorResolutionPolicy: lp.errorResolution?.policy ?? ApiLpErrorResolutionPolicy.Manual,
      resetSeqNumFlagSupported: lp.errorResolution?.resetSeqNumFlagSupported ?? false,
      pricing: LpTranslator.fromApiConnection(lp.pricing),
      trading: LpTranslator.fromApiConnection(lp.trading),
      gateway1: lp.gateways?.[0]?.id,
      gateway2: lp.gateways?.[1]?.id,
      gateway3: lp.gateways?.[2]?.id,
    };
  }

  export function toApiConnection(lpConnectionFormData: ILpConnectionForm): IApiLpConnectionBase {
    const adapterSettings: IApiLpAdapterSetting[] = lpConnectionFormData.adapterSettings
      ?.filter(({ key, value }) => key?.trim() && value?.trim())
      .map(({ key, value }) => ({ key, value }));

    return {
      host: lpConnectionFormData.host,
      port: lpConnectionFormData.port,
      login: lpConnectionFormData.login,
      password: lpConnectionFormData.password,
      adapterSettings,
    };
  }

  export function toApi(lp: ILpForm): IAdminApiLpCreate {
    const { gateway1, gateway2, gateway3 } = lp;

    const gateways: IAdminApiLpCreate['gateways'] = [gateway1];
    if (gateway2) {
      gateways.push(gateway2);
    }
    if (gateway3) {
      gateways.push(gateway3);
    }

    // The demo adapter connections are preconfigured on the CP side
    const isDemoAdapter = lp.adapter === DEMO_CBRIDGE_ADAPTER;

    return {
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      throttlingInterval: lp.throttlingInterval ?? undefined,
      errorResolution: {
        policy: lp.errorResolutionPolicy,
        resetSeqNumFlagSupported: lp.resetSeqNumFlagSupported,
      },
      pricing: isDemoAdapter ? undefined : LpTranslator.toApiConnection(lp.pricing),
      trading: isDemoAdapter ? undefined : LpTranslator.toApiConnection(lp.trading),
      gateways,
    };
  }

  export function toEditableAdapterSettings(lp: ILpForm = {}) {
    const { pricing, trading } = lp;
    const pricingSettings = pricing?.adapterSettings ?? [];
    const tradingSettings = trading?.adapterSettings ?? [];

    return {
      ...lp,
      pricing: {
        ...pricing,
        adapterSettings: [...pricingSettings, { id: pricingSettings.length }],
      },
      trading: {
        ...trading,
        adapterSettings: [...tradingSettings, { id: tradingSettings.length }],
      },
    };
  }
}
